import { Injectable } from '@angular/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { FilterDTO, FilterOperator, FleetListClient, FleetListEntryDTO, PagedResultDTOOfFleetListEntryDTO, PagedResultDTOOfParkerCompanyDTO, ParkerCompanyClient, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { ClientService } from 'src/app/base/services/client/client.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable({
  providedIn: 'root'
})
export class FleetListService extends ShowInactiveService implements IAdaptorService {

  public selectedCompanyId: number = null;

  constructor(private fleetListClient: FleetListClient,
    private toastService: ToastService,
    private clientService: ClientService,
    private parkerCompanyClient: ParkerCompanyClient) {
    super();
  }

  getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<PagedResultDTOOfFleetListEntryDTO> {
    if (this.clientService.selectedClient == null || this.clientService.selectedClient.id == null) {
      return throwError(() => new Error('Client is not selected'));
    }
    if (!filters) {
      filters = [];
    }
    return this.fleetListClient.searchFleetList(this.clientService.selectedClient.id, this.selectedCompanyId ?? null, filters, skip, take, sortBy, sortDirection)
      .pipe(
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  addRecord(data: FleetListEntryDTO): Observable<FleetListEntryDTO> {
    return this.fleetListClient.createFleetListEntry(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.administration.fleet-list.messages.add-title',
          'i18n.domain.administration.fleet-list.messages.add-message'
        );
      }),
      catchError((error) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      }));
  }

  updateRecord(data: FleetListEntryDTO): Observable<FleetListEntryDTO> {
    return this.fleetListClient.updateFleetListEntry(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.administration.fleet-list.messages.update-title',
          'i18n.domain.administration.fleet-list.messages.update-message'
        );
      }),
      catchError(error => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      }));
  }

  deleteRecord(id: number): Observable<void> {
    return this.fleetListClient.deleteFleetListEntry(id).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.administration.fleet-list.messages.delete-title',
          'i18n.domain.administration.fleet-list.messages.delete-message'
        );
      }),
      catchError(error => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public searchCompanyDropdownValues(clientId: number, searchText: string): Observable<PagedResultDTOOfParkerCompanyDTO> {
    const filterDTOs = [];
    filterDTOs.push(new FilterDTO({
      isComplex: false,
      field: 'ClientId',
      operator: FilterOperator.Equal,
      value: clientId.toString(),
    }));
    filterDTOs.push(new FilterDTO({
      isComplex: false,
      field: 'Name',
      operator: FilterOperator.Contains,
      value: searchText,
    }));
    return this.parkerCompanyClient.searchParkerCompanies(this.clientService.selectedClient.id, filterDTOs, 0, 9, 'Name', SortDirection.Ascending)
      .pipe(
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }
}
